'use client';
import { useState } from 'react';
import { Container } from 'react-bootstrap';
import { useManual } from '../../apps/hooks/manual/useManual';
import { ManualList } from './components/ManualList';
import { ManualDetailModal } from './components/ManualDetailModal';
import { CreateManualModal } from './components/CreateManualModal';
import { UpdateManualModal } from './components/UpdateManualModal';
import { DeleteManualModal } from './components/DeleteManualModal';
import { PageHeader } from './components/PageHeader';
import type { Manual, UpdateManualRequest, CreateManualRequest } from '../../infrastructure/ManualClient';

export default function ManualPage() {
  const { manualsQuery, createManualMutation, updateManualMutation, deleteManualMutation, error } = useManual();

  const [selectedManual, setSelectedManual] = useState<Manual | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: manuals = [], isLoading } = manualsQuery;

  const handleManualSelect = (manual: Manual) => {
    setSelectedManual(manual);
    setShowDetailModal(true);
  };

  const handleEditManual = (manual: Manual) => {
    setSelectedManual(manual);
    setShowUpdateModal(true);
  };

  const handleDeleteManual = (manual: Manual) => {
    setSelectedManual(manual);
    setShowDeleteModal(true);
  };

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  return (
    <Container className="py-4">
      <PageHeader title="MCP Manual Management" onCreateNew={handleCreateNew} />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <ManualList
        manuals={manuals}
        loading={isLoading}
        onView={handleManualSelect}
        onEdit={handleEditManual}
        onDelete={handleDeleteManual}
      />

      {selectedManual && (
        <>
          <ManualDetailModal
            show={showDetailModal}
            manual={selectedManual}
            onHide={() => setShowDetailModal(false)}
            onEdit={() => {
              setShowDetailModal(false);
              setShowUpdateModal(true);
            }}
          />

          <UpdateManualModal
            show={showUpdateModal}
            manual={selectedManual}
            onHide={() => setShowUpdateModal(false)}
            onSubmit={(data: UpdateManualRequest) => {
              updateManualMutation.mutate({
                manualId: selectedManual.manualId,
                data,
              });
              setShowUpdateModal(false);
            }}
            isSubmitting={updateManualMutation.isPending}
          />

          <DeleteManualModal
            show={showDeleteModal}
            manual={selectedManual}
            onHide={() => setShowDeleteModal(false)}
            onConfirm={() => {
              deleteManualMutation.mutate(selectedManual.manualId);
              setShowDeleteModal(false);
            }}
            isDeleting={deleteManualMutation.isPending}
          />
        </>
      )}

      <CreateManualModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={(data: CreateManualRequest) => {
          createManualMutation.mutate(data);
          setShowCreateModal(false);
        }}
        isSubmitting={createManualMutation.isPending}
      />
    </Container>
  );
}
